import {
  CreditTransaction,
  TransactionSources,
  TransactionTypes,
  ConsumptionReason,
  CreditTransactionSource,
  CreditTransactionAction,
  TransactionContext,
} from "@custom-types/credit-types";

/** @returns True if the object type is Credit Transaction */
export function isCreditTransaction(data: unknown): data is CreditTransaction {
  if (!data || typeof data !== "object") {
    return false;
  }
  const toCheck: Partial<CreditTransaction> = data;
  return (
    typeof toCheck.id === "string" &&
    typeof toCheck.timestamp === "string" &&
    isCreditTransactionSource(toCheck.source) &&
    isCreditTransactionAction(toCheck.action) &&
    isCreditTransactionContext(toCheck.context) &&
    isCreditTransactionResult(toCheck.result) &&
    Array.isArray(toCheck.tags) &&
    (toCheck.visibility === "External" || toCheck.visibility === "Internal")
  );
}

/** @returns True if the object type is Credit Transaction Source */
export function isCreditTransactionSource(
  source: unknown
): source is { type: TransactionSources; version: string } {
  if (!source || typeof source !== "object") {
    return false;
  }
  const s: Partial<CreditTransactionSource> = source;
  return (
    typeof s.type === "string" &&
    Object.values(TransactionSources).includes(s.type) &&
    typeof s.version === "string"
  );
}

/** @returns True if the object type is Credit Transaction Action */
export function isCreditTransactionAction(
  action: unknown
): action is CreditTransaction["action"] {
  if (!action || typeof action !== "object") {
    return false;
  }
  const a: Partial<CreditTransactionAction> = action;
  const data: Record<string, unknown> | undefined = a.data;
  return (
    typeof a.type === "string" &&
    Object.values(TransactionTypes).includes(a.type) &&
    data !== undefined &&
    typeof data.tokenAmount === "number" &&
    (data.consumptionReason === undefined ||
      Object.values(ConsumptionReason).includes(
        data.consumptionReason as ConsumptionReason
      ))
  );
}

/** @returns True if the object type is Credit Transaction Context */
export function isCreditTransactionContext(
  context: unknown
): context is CreditTransaction["context"] {
  if (!context || typeof context !== "object") {
    return false;
  }
  const c: Partial<TransactionContext> = context;
  return typeof c.companyId === "string";
}

/** @returns True if the object type is Credit Transaction Result */
export function isCreditTransactionResult(
  result: unknown
): result is CreditTransaction["result"] {
  if (!result || typeof result !== "object") {
    return false;
  }

  const r: Partial<Record<string, unknown>> = result;
  const data =
    r.data && typeof r.data === "object" && !Array.isArray(r.data)
      ? (r.data as Record<string, unknown>)
      : undefined;

  return (
    typeof r.type === "string" &&
    [
      "TokensInserted",
      "TokenInsertionRejected",
      "TokenInsertionFailed",
      "TokensConsumed",
      "TokenConsumptionRejected",
      "TokenConsumptionFailed",
      "TokensExpired",
    ].includes(r.type) &&
    data !== undefined &&
    Array.isArray(data.subscriptionIds)
  );
}
